import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { MembershipTier, PlatformRole, VerificationStatus } from '@/auth/types';
import AccountLayout from '@/components/feature/AccountLayout';
import { useAuth } from '@/hooks/useAuth';
import { fetchBindEmail, fetchSendEmailBindingCode, fetchChangePhone, fetchSendPhoneBindingCode, fetchSendOldPhoneVerifyCode, fetchVerifyOldPhone } from '@/service/api/account';
import { useAuthStore } from '@/stores/authStore';
import { ImageCropModal, CropResult, Toast, SendCodeButton } from '@/components/common';
import { uploadFile } from '@/utils/upload';

const roleLabels: Record<PlatformRole, string> = {
  creator: '创作者',
  client: '需求方',
  enterprise_manager: '企业管理员',
  admin: '平台管理员',
};

const membershipConfig: Record<MembershipTier, { label: string; className: string; description: string }> = {
  free: {
    label: '免费会员',
    className: 'bg-background-100 text-foreground-600',
    description: '基础平台服务',
  },
  pro: {
    label: '专业会员',
    className: 'bg-primary-100 text-primary-700',
    description: '已解锁会员课程与人才联系',
  },
  enterprise: {
    label: '企业会员',
    className: 'bg-accent-100 text-accent-700',
    description: '已解锁企业项目与成员能力',
  },
};

const verificationConfig: Record<VerificationStatus, { label: string; className: string; description: string }> = {
  unverified: {
    label: '未认证',
    className: 'bg-background-100 text-foreground-600',
    description: '完成认证后可使用更多平台服务',
  },
  pending: {
    label: '审核中',
    className: 'bg-accent-100 text-accent-700',
    description: '认证资料正在审核，请耐心等待',
  },
  verified: {
    label: '已认证',
    className: 'bg-primary-100 text-primary-700',
    description: '账号主体信息已通过认证',
  },
  rejected: {
    label: '认证未通过',
    className: 'bg-red-50 text-red-600',
    description: '请修改资料后重新提交认证',
  },
};

function maskPhone(phone?: string) {
  if (!phone || !/^\d{11}$/.test(phone)) return phone || '未绑定';
  return `${phone.slice(0, 3)}****${phone.slice(-4)}`;
}

export default function ProfilePage() {
  const { user, rawUser, can, updateProfile, isEnterprise } = useAuth();
  const fetchCurrentAccount = useAuthStore((state) => state.fetchCurrentAccount);
  const loginWithSessionResponse = useAuthStore((state) => state.loginWithSessionResponse);

  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const [form, setForm] = useState({
    name: user?.nick_name || user?.name || '',
  });

  // 头像裁剪与上传相关状态
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  // 绑定邮箱 state
  const [isBindingEmail, setIsBindingEmail] = useState(false);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', code: '' });
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // 绑定/换绑手机号 state
  const [isBindingPhone, setIsBindingPhone] = useState(false);
  const [phoneStep, setPhoneStep] = useState<1 | 2>(1);
  const [oldPhoneCode, setOldPhoneCode] = useState('');
  const [oldPhoneVerificationToken, setOldPhoneVerificationToken] = useState('');
  const [sendingOldPhoneCode, setSendingOldPhoneCode] = useState(false);
  const [verifyingOldPhone, setVerifyingOldPhone] = useState(false);
  const [oldPhoneCountdown, setOldPhoneCountdown] = useState(0);

  const [phoneForm, setPhoneForm] = useState({ phone: '', code: '' });
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [phoneBindingLoading, setPhoneBindingLoading] = useState(false);
  const [phoneCountdown, setPhoneCountdown] = useState(0);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');

  if (!user) return null;

  const membership = membershipConfig[user.membership];

  // 从 subscriptions[0].plan_name 展示名字，如果没有就是免费会员
  const planName =
    rawUser?.membership?.subscriptions?.[0]?.plan_name ||
    (user as any)?.membership?.subscriptions?.[0]?.plan_name ||
    membership.label;
  const planDescription = membership.description;

  // 根据 certification_status 进行判断，为 APPROVED 时为通过
  const getVerificationInfo = () => {
    const certStatus = (rawUser?.certification_status || user?.certification_status || '').toUpperCase();
    if (certStatus === 'APPROVED' || certStatus === 'PASSED' || certStatus === 'SUCCESS') {
      return verificationConfig.verified;
    }
    if (certStatus === 'PENDING' || certStatus === 'AUDITING' || certStatus === 'SUBMITTED') {
      return verificationConfig.pending;
    }
    if (certStatus === 'REJECTED' || certStatus === 'FAILED') {
      return verificationConfig.rejected;
    }
    return verificationConfig[user.verificationStatus] || verificationConfig.unverified;
  };

  const verification = getVerificationInfo();
  const isMarketActive = (rawUser?.market_residency_status || user?.market_residency_status) === 'ACTIVE';
  const isNicknameEditable = (rawUser?.nickname_editable ?? user?.nickname_editable) !== false;

  // 选择本地文件触发裁剪
  const handleSelectAvatarFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedAvatarFile(files[0]);
      setCropModalOpen(true);
    }
    e.target.value = '';
  };

  // 保存裁剪后的头像
  const handleAvatarCrop = async (result: CropResult) => {
    setIsUploadingAvatar(true);
    try {
      // 1. 直传至 OSS 服务 (使用 ACCOUNT 场景，自动推导 public-when-published 策略)
      const uploadRes = await uploadFile(result.file, 'ACCOUNT');
      const assetId = uploadRes.asset_id || uploadRes.upload_asset_id;
      const avatarUrl = uploadRes.file_url || result.dataUrl;

      // 2. 更新个人中心 Profile 头像
      await updateProfile({
        avatar: avatarUrl,
        avatar_asset_id: assetId ? String(assetId) : undefined,
      });
      await fetchCurrentAccount();
      setSaved(true);
      setToast({ message: '头像更新成功！', type: 'success' });
    } catch (err) {
      console.error('上传头像出错:', err);
      const errMsg = err instanceof Error ? err.message : '头像上传失败，请重试';
      setToast({ message: errMsg, type: 'error' });
    } finally {
      setIsUploadingAvatar(false);
      setCropModalOpen(false);
      setSelectedAvatarFile(null);
    }
  };

  const handleEdit = () => {
    setForm({
      name: user.nick_name || user.name,
    });
    setSaved(false);
    setIsEditing(true);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    setIsSavingNickname(true);
    try {
      await updateProfile({
        nick_name: name,
        avatar_asset_id: undefined,
      });
      await fetchCurrentAccount();
      setIsEditing(false);
      setSaved(true);
      setToast({ message: '昵称修改成功！', type: 'success' });
    } catch (err) {
      console.error('修改昵称失败:', err);
      const errMsg = err instanceof Error ? err.message : '修改昵称失败，请重试';
      setToast({ message: errMsg, type: 'error' });
    } finally {
      setIsSavingNickname(false);
    }
  };

  // 发送邮箱验证码
  const handleSendEmailCode = async (): Promise<boolean> => {
    setEmailError('');
    setEmailSuccess('');
    const email = emailForm.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('请输入有效的电子邮箱地址');
      return false;
    }

    try {
      await fetchSendEmailBindingCode({ email });
      setEmailSuccess('验证码已发送至您的邮箱，请查收');
      return true;
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : '发送验证码失败，请稍后重试');
      return false;
    }
  };

  // 绑定邮箱提交
  const handleBindEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailError('');
    setEmailSuccess('');

    const email = emailForm.email.trim();
    const verification_code = emailForm.code.trim();

    if (!email) {
      setEmailError('请输入电子邮箱地址');
      return;
    }
    if (!verification_code) {
      setEmailError('请输入验证码');
      return;
    }

    setBindingLoading(true);
    try {
      await fetchBindEmail({ email, verification_code });
      await fetchCurrentAccount();
      setEmailSuccess('电子邮箱绑定成功！');
      setTimeout(() => {
        setIsBindingEmail(false);
        setEmailSuccess('');
        setEmailForm({ email: '', code: '' });
      }, 1500);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : '绑定电子邮箱失败，请检查验证码');
    } finally {
      setBindingLoading(false);
    }
  };

  // 发送当前/旧手机号验证码
  const handleSendOldPhoneCode = async (): Promise<boolean> => {
    setPhoneError('');
    setPhoneSuccess('');
    try {
      await fetchSendOldPhoneVerifyCode();
      setPhoneSuccess('验证码已发送至您的原手机号，请查收');
      return true;
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : '发送验证码失败，请稍后重试');
      return false;
    }
  };

  // 校验当前/旧手机号验证码并获取换绑 token
  const handleVerifyOldPhone = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPhoneError('');
    setPhoneSuccess('');

    const verification_code = oldPhoneCode.trim();
    if (!verification_code) {
      setPhoneError('请输入验证码');
      return;
    }

    setVerifyingOldPhone(true);
    try {
      const res = await fetchVerifyOldPhone({ verification_code });
      if (res.verification_token) {
        setOldPhoneVerificationToken(res.verification_token);
        setPhoneStep(2);
        setPhoneSuccess('原手机号验证成功，请输入新手机号码');
      } else {
        setPhoneError('验证失败，未获取到换绑凭证');
      }
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : '原手机号验证失败，请检查验证码');
    } finally {
      setVerifyingOldPhone(false);
    }
  };

  // 发送新手机号验证码
  const handleSendPhoneCode = async (): Promise<boolean> => {
    setPhoneError('');
    setPhoneSuccess('');
    const phone = phoneForm.phone.trim();
    if (!phone || !/^\d{11}$/.test(phone)) {
      setPhoneError('请输入有效的 11 位手机号码');
      return false;
    }

    try {
      await fetchSendPhoneBindingCode({ phone });
      setPhoneSuccess('验证码已发送至新手机号，请查收');
      return true;
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : '发送验证码失败，请稍后重试');
      return false;
    }
  };

  // 换绑/绑定手机号提交
  const handleBindPhone = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPhoneError('');
    setPhoneSuccess('');

    const phone = phoneForm.phone.trim();
    const verification_code = phoneForm.code.trim();

    if (!phone || !/^\d{11}$/.test(phone)) {
      setPhoneError('请输入有效的 11 位手机号码');
      return;
    }
    if (!verification_code) {
      setPhoneError('请输入验证码');
      return;
    }

    setPhoneBindingLoading(true);
    try {
      const res = await fetchChangePhone({
        old_verification_token: oldPhoneVerificationToken,
        phone,
        verification_code,
      });
      await loginWithSessionResponse(res);
      setPhoneSuccess('手机号修改成功！');
      setTimeout(() => {
        setIsBindingPhone(false);
        setPhoneStep(1);
        setOldPhoneCode('');
        setOldPhoneVerificationToken('');
        setPhoneSuccess('');
        setPhoneForm({ phone: '', code: '' });
      }, 1500);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : '修改手机号失败，请检查验证码');
    } finally {
      setPhoneBindingLoading(false);
    }
  };

  const secondaryQuickLink = isEnterprise
    ? { href: '/my-postings', icon: 'ri-survey-line', title: '需求管理', description: '管理企业发布的需求' }
    : { href: '/my-resume', icon: 'ri-file-user-line', title: '我的简历', description: '完善个人履历与作品' };

  return (
    <AccountLayout activePath="/profile" breadcrumbs={[{ label: '账号中心' }]}>
      <section className="glass-card rounded-2xl p-6 md:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 pb-6 border-b border-background-200/70">
          <div className="flex items-center gap-4 min-w-0">
            <div className="relative group w-16 h-16 shrink-0 rounded-full bg-primary-500 overflow-hidden flex items-center justify-center border-2 border-white/40 shadow-md">
              {user.avatar && (user.avatar.startsWith('http') || user.avatar.startsWith('data:') || user.avatar.startsWith('blob:') || user.avatar.includes('/')) ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover rounded-full" />
              ) : (
                <span className="text-white font-bold text-xl">{user.avatar || user.name.slice(0, 1)}</span>
              )}
              {isUploadingAvatar ? (
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white text-[10px] font-medium z-10">
                  <i className="ri-loader-4-line animate-spin text-base mb-0.5" aria-hidden="true"></i>
                  <span>上传中...</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => avatarFileInputRef.current?.click()}
                  className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity text-white text-[10px] font-medium cursor-pointer"
                  title="点击更换头像"
                >
                  <i className="ri-camera-line text-base mb-0.5" aria-hidden="true"></i>
                  <span>更换头像</span>
                </button>
              )}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading font-bold text-xl text-foreground-950 truncate">{user.name}</h1>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${membership.className}`}>
                  {planName}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {user.roles.map((role) => (
                  <span key={role} className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-600">
                    {roleLabels[role]}
                  </span>
                ))}
              </div>
              <p className="text-xs text-foreground-400 mt-2">账号 ID：{user.id}</p>
            </div>
          </div>
          {!isEditing && (
            <button
              type="button"
              onClick={handleEdit}
              className="shrink-0 px-4 py-2 rounded-lg border border-primary-300/60 text-primary-700 text-sm font-medium hover:bg-primary-50 active:brightness-95 transition-all"
            >
              编辑资料
            </button>
          )}
        </div>

        {saved && (
          <div className="mt-5 px-4 py-3 rounded-lg bg-primary-50 text-primary-700 text-xs flex items-center gap-2">
            <i className="ri-check-line" aria-hidden="true"></i>
            资料已保存到当前登录会话
          </div>
        )}

        {isEditing ? (
          <form onSubmit={handleSave} className="mt-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-700 mb-1.5">{isEnterprise ? '主体名称' : '用户昵称'}</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="请输入昵称"
                  disabled={!isNicknameEditable || isMarketActive || isSavingNickname}
                  className="w-full px-4 py-3 rounded-lg border border-background-300 bg-white text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:bg-background-100 disabled:text-foreground-400 disabled:cursor-not-allowed"
                  required
                />
                {isMarketActive ? (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    已入驻创作者/服务商大厅，暂不支持修改昵称
                  </p>
                ) : !isNicknameEditable ? (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    暂不支持修改昵称
                  </p>
                ) : null}
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isSavingNickname || isMarketActive || !isNicknameEditable}
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 active:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSavingNickname ? '保存中...' : '保存修改'}
              </button>
              <button type="button" onClick={() => setIsEditing(false)} className="px-5 py-2.5 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-gray-50 cursor-pointer">
                取消
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mt-6">
            <InfoField label={isEnterprise ? '主体名称' : '用户昵称'} value={user.nick_name || user.name} />
            <InfoField label="手机号" value={maskPhone(user.phone)} />
            <InfoField label="账号类型" value={isEnterprise ? '企业账号' : '个人账号'} />
            <InfoField label="认证状态" value={verification.label} />
          </div>
        )}

        {/* 电子邮箱绑定 - 独立一行 */}
        <div className="mt-6 pt-6 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground-900">电子邮箱</h3>
                {user.email ? (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium inline-flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i> 已绑定
                  </span>
                ) : (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium inline-flex items-center gap-1">
                    <i className="ri-error-warning-line text-amber-500"></i> 未绑定
                  </span>
                )}
              </div>
              <p className="text-xs text-foreground-400 mt-1">
                {user.email ? `当前绑定的邮箱：${user.email}` : '绑定电子邮箱后可用于接收平台重要通知与相关验证'}
              </p>
            </div>

            {!isBindingEmail && (
              <button
                type="button"
                onClick={() => {
                  setEmailForm({ email: user.email || '', code: '' });
                  setEmailError('');
                  setEmailSuccess('');
                  setIsBindingEmail(true);
                }}
                className="self-start sm:self-auto px-4 py-2 rounded-lg border border-primary-300/60 text-primary-700 text-xs font-medium hover:bg-primary-50 active:brightness-95 transition-all shrink-0 cursor-pointer"
              >
                {user.email ? '更换邮箱' : '绑定邮箱'}
              </button>
            )}
          </div>

          {/* 邮箱绑定/更换表单 */}
          {isBindingEmail && (
            <div className="mt-4 p-5 rounded-xl bg-background-50 border border-background-200 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-foreground-800">
                  {user.email ? '更换电子邮箱' : '绑定电子邮箱'}
                </h4>
                <button
                  type="button"
                  onClick={() => setIsBindingEmail(false)}
                  className="text-foreground-400 hover:text-foreground-600 text-xs cursor-pointer"
                >
                  取消
                </button>
              </div>

              {emailError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line"></i> {emailError}
                </div>
              )}

              {emailSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600 text-xs flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i> {emailSuccess}
                </div>
              )}

              <form onSubmit={handleBindEmail} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱地址</label>
                    <input
                      type="email"
                      value={emailForm.email}
                      onChange={(e) => setEmailForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="请输入邮箱地址（如 example@domain.com）"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-white text-xs outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱验证码</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={emailForm.code}
                        onChange={(e) => setEmailForm((prev) => ({ ...prev, code: e.target.value }))}
                        placeholder="请输入6位验证码"
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-white text-xs outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        maxLength={6}
                        required
                      />
                      <SendCodeButton
                        onSend={handleSendEmailCode}
                        disabled={!emailForm.email.trim()}
                        countingText="{s}s 后重新获取"
                        className="shrink-0 px-3.5 py-2.5 rounded-lg border border-primary-300 text-primary-700 text-xs font-medium hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={bindingLoading || !emailForm.email.trim() || !emailForm.code.trim()}
                    className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:brightness-95 active:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {bindingLoading ? '提交中...' : '确认绑定'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsBindingEmail(false)}
                    className="px-5 py-2 rounded-lg border border-background-300 text-foreground-600 text-xs hover:bg-gray-100 cursor-pointer"
                  >
                    取消
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* 手机号码换绑/绑定 - 独立一行 */}
        <div className="mt-6 pt-6 border-t border-background-200/70">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground-900">手机号码</h3>
                {user.phone ? (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium inline-flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-primary-500"></i> 已绑定
                  </span>
                ) : (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium inline-flex items-center gap-1">
                    <i className="ri-error-warning-line text-amber-500"></i> 未绑定
                  </span>
                )}
              </div>
              <p className="text-xs text-foreground-400 mt-1">
                {user.phone ? `当前绑定的手机号：${maskPhone(user.phone)}` : '绑定手机号码后可用于登录及接收重要服务短信通知'}
              </p>
            </div>

            {!isBindingPhone && (
              <button
                type="button"
                onClick={() => {
                  setPhoneForm({ phone: '', code: '' });
                  setPhoneError('');
                  setPhoneSuccess('');
                  setIsBindingPhone(true);
                }}
                className="self-start sm:self-auto px-4 py-2 rounded-lg border border-primary-300/60 text-primary-700 text-xs font-medium hover:bg-primary-50 active:brightness-95 transition-all shrink-0 cursor-pointer"
              >
                {user.phone ? '更换手机号' : '绑定手机号'}
              </button>
            )}
          </div>

          {/* 手机号换绑/绑定表单 */}
          {isBindingPhone && (
            <div className="mt-4 p-5 rounded-xl bg-background-50 border border-background-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-bold text-foreground-800">
                    {user.phone ? '更换手机号码' : '绑定手机号码'}
                  </h4>
                  {user.phone && (
                    <span className="text-[10px] text-foreground-500 bg-background-200/60 px-2 py-0.5 rounded">
                      步骤 {phoneStep} / 2：{phoneStep === 1 ? '验证原手机号' : '绑定新手机号'}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsBindingPhone(false);
                    setPhoneStep(1);
                    setOldPhoneCode('');
                    setOldPhoneVerificationToken('');
                  }}
                  className="text-foreground-400 hover:text-foreground-600 text-xs cursor-pointer"
                >
                  取消
                </button>
              </div>

              {phoneError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line"></i> {phoneError}
                </div>
              )}

              {phoneSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 text-emerald-600 text-xs flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i> {phoneSuccess}
                </div>
              )}

              {user.phone && phoneStep === 1 ? (
                /* Step 1: 验证原手机号 */
                <form onSubmit={handleVerifyOldPhone} className="space-y-4">
                  <div className="text-xs text-foreground-600 mb-2">
                    为了保障您的账号安全，更换手机号前请先对原手机号进行身份验证：
                    <span className="font-semibold text-foreground-900 ml-1">{maskPhone(user.phone)}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">原手机号验证码</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={oldPhoneCode}
                          onChange={(e) => setOldPhoneCode(e.target.value)}
                          placeholder="请输入6位验证码"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-white text-xs outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                          maxLength={6}
                          required
                        />
                        <SendCodeButton
                          onSend={handleSendOldPhoneCode}
                          countingText="{s}s 后重新获取"
                          className="shrink-0 px-3.5 py-2.5 rounded-lg border border-primary-300 text-primary-700 text-xs font-medium hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={verifyingOldPhone || !oldPhoneCode.trim()}
                      className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:brightness-95 active:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {verifyingOldPhone ? '验证中...' : '下一步：绑定新手机号'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsBindingPhone(false)}
                      className="px-5 py-2 rounded-lg border border-background-300 text-foreground-600 text-xs hover:bg-gray-100 cursor-pointer"
                    >
                      取消
                    </button>
                  </div>
                </form>
              ) : (
                /* Step 2: 绑定新手机号 */
                <form onSubmit={handleBindPhone} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">新手机号码</label>
                      <input
                        type="tel"
                        value={phoneForm.phone}
                        onChange={(e) => setPhoneForm((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder="请输入 11 位手机号码"
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-white text-xs outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                        maxLength={11}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">短信验证码</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={phoneForm.code}
                          onChange={(e) => setPhoneForm((prev) => ({ ...prev, code: e.target.value }))}
                          placeholder="请输入6位验证码"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-white text-xs outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                          maxLength={6}
                          required
                        />
                        <SendCodeButton
                          onSend={handleSendPhoneCode}
                          disabled={!/^\d{11}$/.test(phoneForm.phone.trim())}
                          countingText="{s}s 后重新获取"
                          className="shrink-0 px-3.5 py-2.5 rounded-lg border border-primary-300 text-primary-700 text-xs font-medium hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={phoneBindingLoading || !/^\d{11}$/.test(phoneForm.phone.trim()) || !phoneForm.code.trim()}
                      className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:brightness-95 active:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {phoneBindingLoading ? '提交中...' : '确认绑定'}
                    </button>
                    {user.phone && (
                      <button
                        type="button"
                        onClick={() => setPhoneStep(1)}
                        className="px-5 py-2 rounded-lg border border-background-300 text-foreground-600 text-xs hover:bg-gray-100 cursor-pointer"
                      >
                        返回上一步
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsBindingPhone(false)}
                      className="px-5 py-2 rounded-lg border border-background-300 text-foreground-600 text-xs hover:bg-gray-100 cursor-pointer"
                    >
                      取消
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Link to="/certification" className="glass-card rounded-xl p-5 hover:border-primary-200 transition-all">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-primary-600 text-lg" aria-hidden="true"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{isEnterprise ? '企业认证' : '实名认证'}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{verification.description}</p>
              </div>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full shrink-0 ${verification.className}`}>{verification.label}</span>
          </div>
        </Link>

        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
              <i className="ri-vip-crown-line text-accent-600 text-lg" aria-hidden="true"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-950">{planName}</p>
              <p className="text-xs text-foreground-400 mt-0.5">{planDescription}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <QuickLink href="/certification" icon="ri-shield-user-line" title="认证中心" description="查看主体认证状态" />
        <QuickLink {...secondaryQuickLink} />
        <QuickLink href="/settings" icon="ri-lock-line" title="安全设置" description="管理账号与登录安全" />
      </section>

      {/* 隐藏的头像上传选择框 */}
      <input
        ref={avatarFileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleSelectAvatarFile}
      />

      {/* 头像裁剪弹窗 (默认 1:1 圆形裁剪) */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          setSelectedAvatarFile(null);
        }}
        image={selectedAvatarFile}
        onCrop={handleAvatarCrop}
        circular={true}
        aspectRatio={1}
        allowRatioSelect={false}
        title="裁剪用户头像"
        description="拖动或缩放调整头像在 1:1 圆形区域内的展示"
        loading={isUploadingAvatar}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AccountLayout>
  );
}

function InfoField({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <p className="text-xs text-foreground-400 mb-1">{label}</p>
      <p className={`text-sm ${muted ? 'text-foreground-500' : 'text-foreground-900'}`}>{value}</p>
    </div>
  );
}

function QuickLink({ href, icon, title, description }: { href: string; icon: string; title: string; description: string }) {
  return (
    <Link to={href} className="glass-card rounded-xl p-5 hover:border-primary-200 transition-all flex items-center gap-3">
      <div className="w-10 h-10 shrink-0 rounded-lg bg-background-100 flex items-center justify-center">
        <i className={`${icon} text-foreground-600 text-lg`} aria-hidden="true"></i>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950">{title}</p>
        <p className="text-xs text-foreground-400 mt-0.5 truncate">{description}</p>
      </div>
    </Link>
  );
}
